<script lang="ts" setup>
import { computed, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  /** mp4 视频地址，为空时直接降级为封面图 */
  src?: string
  /** 封面兜底图 */
  poster?: string
  /** 是否处于激活状态（当前 slide / 当前 tab），驱动播放与暂停 */
  active?: boolean
  /** 是否显示底部播放进度条 */
  showProgress?: boolean
  /** 是否显示右上角声音开关按钮 */
  showMuteButton?: boolean
}>(), {
  src: '',
  poster: '',
  active: true,
  showProgress: false,
  showMuteButton: false,
})

const emit = defineEmits<{
  (e: 'progress', percent: number): void
  (e: 'fallback'): void
}>()

const videoRef = ref<HTMLVideoElement | null>(null)
const fallback = ref(false)
const playing = ref(false)
const progress = ref(0)
const isMuted = ref(true)

const showVideo = computed(() => !!props.src && !fallback.value)

// 点击声音按钮：切换静音（用户手势内取消静音是合法的）
function toggleMute() {
  const video = videoRef.value
  if (!video)
    return
  video.muted = !video.muted
  isMuted.value = video.muted
}

function play() {
  const video = videoRef.value
  if (!video || !showVideo.value)
    return
  // Vue 2 模板的 muted attribute 不可靠，播放前强制设属性，保证移动端静音自动播放
  video.muted = true
  isMuted.value = true
  const result = video.play()
  result?.then(() => {
    playing.value = true
  }).catch(() => {
    enterFallback()
  })
}

function pause(reset = false) {
  const video = videoRef.value
  if (!video)
    return
  video.pause()
  playing.value = false
  if (reset) {
    video.currentTime = 0
    progress.value = 0
  }
}

function enterFallback() {
  if (fallback.value)
    return
  fallback.value = true
  playing.value = false
  emit('fallback')
}

function onTimeUpdate() {
  const video = videoRef.value
  if (!video || !video.duration)
    return
  progress.value = (video.currentTime / video.duration) * 100
  emit('progress', progress.value)
}

watch(() => props.active, (val) => {
  val ? play() : pause(true)
})

watch(() => props.src, async () => {
  fallback.value = false
  progress.value = 0
  // 此前已 fallback 时 <video> 被 v-if 卸载，需等 DOM 重建后 videoRef 才有值
  await nextTick()
  if (props.active)
    play()
})

function onVisibilityChange() {
  if (document.hidden)
    pause()
  else if (props.active)
    play()
}

onMounted(() => {
  document.addEventListener('visibilitychange', onVisibilityChange)
  if (props.active)
    play()
})

onActivated(() => {
  if (props.active)
    play()
})

onDeactivated(() => {
  pause()
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
  pause()
})
</script>

<template>
  <div class="biz-drama-video relative w-full h-full overflow-hidden">
    <video
      v-if="showVideo"
      ref="videoRef"
      :src="src"
      :poster="poster"
      class="video-el"
      muted
      loop
      playsinline
      webkit-playsinline
      preload="metadata"
      @timeupdate="onTimeUpdate"
      @error="enterFallback"
    />
    <img v-else-if="poster" :src="poster" class="video-el">
    <!-- 降级/未播放时的覆盖层（播放按钮等） -->
    <slot v-if="!playing" name="cover" />
    <!-- 声音开关：drama-mute-btn 类名供外层（如 Swiper 点击处理）排除跳转 -->
    <button
      v-if="showMuteButton && showVideo"
      class="drama-mute-btn"
      @click.stop="toggleMute"
    >
      <img
        :src="isMuted ? '/images/vowpath/home/icon-video-mute.png' : '/images/vowpath/home/icon-video-sound.png'"
        class="mute-icon"
      >
    </button>
    <div v-if="showProgress && showVideo" class="video-progress">
      <i class="video-progress-inner" :style="{ width: `${progress}%` }" />
    </div>
  </div>
</template>

<style scoped>
.video-el {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.drama-mute-btn {
  position: absolute;
  right: 8px;
  top: 8px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

.mute-icon {
  width: 14px;
  height: 14px;
}

.video-progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  z-index: 2;
}

.video-progress-inner {
  display: block;
  height: 100%;
  background: linear-gradient(270deg, rgba(255, 107, 210, 1) 0%, rgba(161, 98, 255, 1) 100%);
  border-radius: 0 3px 3px 0;
  transition: width 0.2s linear;
}
</style>
